/**
 * SPEC v0.9.5 — the "📋 สรุป" sheet. Reads ONLY what the result-card loaders already
 * fetched (summary-store) and decides the level with decideSummary() (packages/sim).
 * Every row shows a short real value + ● (a reason to worry) / ○ (not), its source +
 * time, and opens the matching detail card when tapped. A dimension that is relevant
 * but failed reads "ไม่มีข้อมูลตอนนี้"; one that is still loading reads "กำลังโหลด…".
 */
import { nearWord, placeWord } from "./place-word";
import { peaksText, trendText } from "./tw-trend";
import { ELEV_LOWLAND_M, LOWLAND_TEXT } from "./elevation-text";
import { riverDisplayLabel } from "./river-names";
import { damToday } from "./dam-layer";
import { summaryLine2011 } from "./dam-2011";
import { decideSummary, depthAdviceCm, RAIN_HEAVY_MM, SUMMARY_RULES, type Dim, type SummaryInputs, type SummaryResult } from "@flowsight/sim";
import type { RainDay } from "./rain-forecast";
import { summaryState, onSummaryChange, hasPoint, type SummaryState } from "./summary-store";
import { emergencyCard } from "./emergency";
import { buildShareUrl, shareToLine } from "./share";
import { telize } from "./depth-ui";
import { fmtCms, thaiDay } from "./dam-change-ui";

const LEVEL_UI: Record<SummaryResult["level"], { icon: string; cls: string }> = {
  flooded: { icon: "🔴", cls: "sum-red" }, prepare: { icon: "🟠", cls: "sum-orange" }, watch: { icon: "🟡", cls: "sum-yellow" },
  clear: { icon: "🟢", cls: "sum-green" }, insufficient: { icon: "⚪", cls: "sum-grey" }, pending: { icon: "⏳", cls: "sum-grey" },
};
const RAIN_TH: Record<string, string> = { none: "ไม่มีฝน", light: "ฝนเล็กน้อย", moderate: "ฝนปานกลาง", heavy: "ฝนหนัก", veryheavy: "ฝนหนักมาก" };
const RANK: Record<string, number> = { none: 0, light: 1, moderate: 2, heavy: 3, veryheavy: 4 };
const THAI_MON = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];

const esc = (s: string): string => s.replace(/[<>&"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[c] ?? c));
const ok = <T>(d: Dim<T>): d is { state: "ok"; value: T } => d.state === "ok";
/** "2026-10-04 05:30" / ISO → "4 ต.ค. 05:30 น." */
function thTime(s: string | null | undefined): string {
  if (!s) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(s);
  if (!m) return s;
  return `${Number(m[3])} ${THAI_MON[Number(m[2]) - 1]}${m[4] ? ` ${m[4]}:${m[5]} น.` : ""}`;
}
const nowTh = (): string => new Date().toLocaleString("th-TH", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

/** "2026-09-30" → "30 ก.ย. 69" (Buddhist-era short year, as ปภ. writes it) */
function thBE(ymd: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd ?? "");
  return m ? `${Number(m[3])} ${THAI_MON[Number(m[2]) - 1]} ${String(Number(m[1]) + 543).slice(-2)}` : null;
}
const ddpmStale = (ymd: string | null | undefined): boolean => {
  const t = Date.parse(`${ymd}T00:00:00+07:00`);
  return Number.isFinite(t) && Date.now() - t > SUMMARY_RULES.ddpmStaleHours * 3600e3;
};
/** D4: in the next 3 days, a day where at least one model reaches "heavy" but the median does not. */
function splitDays(days: RainDay[]): RainDay[] {
  return days.slice(0, 3).filter((d) => (RANK[d.level] ?? 0) < 3 && Object.values(d.perModel).some((v) => v != null && v >= RAIN_HEAVY_MM));
}
const DDPM_URL = "https://www.disaster.go.th/";

/* ── store → decideSummary inputs ─────────────────────────────────────── */
function inputsOf(s: SummaryState): SummaryInputs {
  const map = <A, B>(d: Dim<A>, f: (a: A) => B): Dim<B> => (ok(d) ? { state: "ok", value: f(d.value) } : d);
  return {
    gistda: map(s.gistda, (g) => ({ floodedInRadius: g.floodedInRadius })),
    roadGauge: map(s.roadGauge, (g) => ({ maxDepthCm: g.maxDepthCm, name: g.name })),
    station: map(s.station, (x) => ({ maxOverBankM: x.maxOverBankM, overName: x.overName, nearBank: x.nearBank, nearName: x.nearName, count: x.count,
      outside: x.outside ? { name: x.outside.name, distKm: x.outside.distKm, overBankM: x.outside.status === "over" ? x.outside.diffBankM : null } : null })),
    rain: map(s.rain, (r) => ({ splitHeavyDays: splitDays(r.days).map((d) => d.dow) })),
    advice: s.advice,
    ddpm: map(s.place, (p) => ({ declared: p.declared, dateText: thBE(p.declaredDate), stale: ddpmStale(p.declaredDate) })),
    onwr: map(s.lowerRiver, (l) => l.alerts.length > 0),
    upstreamDam: map(s.upstreamDam, (d) => ({ name: d.name, percent: d.percent, releaseAmber: d.releaseAmber, releaseText: d.releaseText })),
    storm: map(s.storm, (x) => ({ count: x.count, name: x.name })),
  };
}

/* ── rows ─────────────────────────────────────────────────────────────── */
interface Row { icon: string; title: string; text: string; worry: boolean | null; src: string; target: string }
const LOADING_TXT = "กำลังโหลด…", NODATA_TXT = "ไม่มีข้อมูลตอนนี้";
function pendingRow(icon: string, title: string, d: Dim<unknown>, target: string): Row | null {
  if (d.state === "loading") return { icon, title, text: LOADING_TXT, worry: null, src: "", target };
  if (d.state === "error") return { icon, title, text: NODATA_TXT, worry: null, src: "", target };
  return null;
}

function rows(s: SummaryState): Row[] {
  const out: Row[] = [];
  // 🌧 rain over the dam's catchment (only when the point has a related dam)
  if (s.damRain.state !== "na") {
    const p = pendingRow("🌧", "ฝนเหนือเขื่อน", s.damRain, "#dam-rain-host");
    if (p) out.push(p);
    else if (ok(s.damRain) && s.damRain.value.up) {
      const dr = s.damRain.value, days = dr.up!.days.slice(0, 3);
      const top = days.reduce((a, b) => ((RANK[b.level] ?? 0) > (RANK[a.level] ?? 0) ? b : a), days[0]!);
      out.push({ icon: "🌧", title: `ฝนเหนือ${dr.dam}`, worry: (RANK[top.level] ?? 0) >= 3,
        text: `3 วันข้างหน้า: ${RAIN_TH[top.level] ?? top.level} (${top.dow} ${top.min}–${top.max} มม.)`,
        src: `Open-Meteo · อัปเดต ${thTime(dr.updatedAt)}`, target: "#dam-rain-host" });
    }
  }
  // 🔋 upstream dam(s) along the nearest main river (≤30 km — not tied to the radius, D1)
  if (s.upstreamDam.state !== "na") {
    const p = pendingRow("🔋", "เขื่อนต้นทาง", s.upstreamDam, "#dam-change");
    if (p) out.push(p);
    else if (ok(s.upstreamDam)) {
      const d = s.upstreamDam.value;
      const delta = (ch: typeof d.change): string => !ch ? "" : ch.dir === "same" ? ` · เท่ากับ${ch.isYesterday ? "เมื่อวาน" : "รายงาน " + thaiDay(ch.prevDate)}`
        : ch.arrow ? ` · ${ch.dir === "up" ? "▲" : "▼"} จาก ${fmtCms(ch.prev)} (${ch.isYesterday ? "เมื่อวาน" : thaiDay(ch.prevDate)})` : ` · (${ch.isYesterday ? "เมื่อวาน" : thaiDay(ch.prevDate)} ${fmtCms(ch.prev)})`;
      const src = `กรมชลฯ (SWOC)${d.valueDate ? " · ข้อมูล ณ " + thTime(d.valueDate) : ""}`;
      out.push({ icon: "🔋", title: d.name, worry: (d.change?.highlight ?? false) || (d.percent != null && d.percent >= 100),
        text: `${d.percent != null ? `${Math.round(d.percent)}% · ` : ""}${d.outflowCms != null ? `ระบาย ${fmtCms(d.outflowCms)} ลบ.ม./วิ` : "ยังไม่มีรายงานระบาย"}${delta(d.change)}`,
        src, target: "#dam-change" });
      // v0.9.7: a short same-day-2554 line — information only, never a reason for the summary level
      const t54 = damToday(d.name), l54 = t54 ? summaryLine2011(t54) : null;
      if (l54) out.push({ icon: "📅", title: "เทียบปี 2554", worry: null, text: l54, src: "ปี 2554: กรมชลประทาน (RID Reservoir API · CC BY)", target: "#dam-change" });
      if (d.c13) out.push({ icon: "🔋", title: "เขื่อนเจ้าพระยา (C.13)", worry: d.c13.change?.highlight ?? false,
        text: `ระบาย ${fmtCms(d.c13.outflowCms)} ลบ.ม./วิ${delta(d.c13.change)}`, src, target: "#dam-change" });
    }
  }
  // 📏 river near you — the nearest main river (≤30 km) + its nearest station (in the radius, else on that river ≤30 km)
  {
    const riv = ok(s.river) ? s.river.value : null;
    // the name as the river-name data has it (คลอง… / แม่น้ำ… kept as is; a bare name gets "แม่น้ำ")
    const rname = riv?.name ? riverDisplayLabel(riv.name) : `ลำน้ำ${nearWord()}`;
    const title = riv ? `${rname} ห่าง ${riv.distKm.toFixed(1)} กม.` : `แม่น้ำ${nearWord()}`;
    const p = pendingRow("📏", title, s.station, "#head-line");
    if (p) out.push(p);
    else if (ok(s.station)) {
      const x = s.station.value;
      const bankTxt = (st: { status: string; diffBankM: number | null }): string => st.status === "over" && st.diffBankM != null ? `สูงกว่าตลิ่ง ${st.diffBankM.toFixed(2)} ม.`
        : st.status === "near" ? "ใกล้ตลิ่ง" : st.status === "normal" && st.diffBankM != null ? `ต่ำกว่าตลิ่ง ${Math.abs(st.diffBankM).toFixed(2)} ม.`
        : st.status === "normal" ? "ต่ำกว่าตลิ่ง" : "ไม่มีค่าระดับตลิ่งที่ใช้ได้";
      const n = x.nearest ?? x.outside;
      if (!n) out.push({ icon: "📏", title, worry: false, text: riv ? `ไม่มีสถานีวัดระดับน้ำบนลำน้ำนี้ภายใน ${SUMMARY_RULES.riverMaxKm} กม.` : `ไม่มีสถานีวัดระดับน้ำในรัศมี ${s.radiusKm} กม.`, src: "ThaiWater (สสน.)", target: "#head-line" });
      else {
        const outsideNote = !x.nearest && x.outside ? " (นอกรัศมี)" : "";
        const other = x.overName && x.overName !== n.name ? ` · ${x.overName} สูงกว่าตลิ่ง ${x.maxOverBankM!.toFixed(2)} ม.` : "";
        out.push({ icon: "📏", title, worry: (x.maxOverBankM ?? -1) >= 0.05 || x.nearBank || (n.status === "over" && (n.diffBankM ?? 0) >= 0.05),
          text: `สถานี ${n.name} ห่าง ${n.distKm.toFixed(1)} กม.${outsideNote}: ${bankTxt(n)}${other}${trendTail(s, n.name)}`, src: `ThaiWater (สสน.) · ${thTime(n.datetime)}`, target: "#head-line" });
      }
    }
  }
  // 📍 your home — satellite · road gauge · rain 3 days · notices
  {
    const g = pendingRow("📍", "ดาวเทียม GISTDA", s.gistda, "#flood-note");
    if (g) out.push(g);
    else if (ok(s.gistda)) {
      const v = s.gistda.value;
      const t = v.level === "flooded" ? "เห็นน้ำท่วมที่จุดนี้" : v.level === "near" && v.nearestKm != null ? `เห็นน้ำท่วมห่าง ~${v.nearestKm.toFixed(1)} กม.` : "ไม่พบน้ำท่วมในภาพล่าสุด (เขตชุมชนตรวจได้จำกัด)";
      out.push({ icon: "📍", title: `${placeWord()} · ดาวเทียม GISTDA`, worry: v.floodedInRadius, text: t, src: `GISTDA${v.observedAt ? " · ภาพ " + thTime(v.observedAt) : ""}`, target: "#flood-note" });
    }
    if (s.roadGauge.state !== "na") {
      const p = pendingRow("📍", "จุดวัดถนน กทม.", s.roadGauge, "#gauge-host");
      if (p) out.push(p);
      else if (ok(s.roadGauge)) {
        const r = s.roadGauge.value;
        out.push({ icon: "📍", title: `${placeWord()} · จุดวัดถนน กทม.`, worry: r.maxDepthCm != null && r.maxDepthCm >= 20,
          text: r.maxDepthCm == null ? `ไม่มีจุดวัดที่มีน้ำท่วมในรัศมี ${s.radiusKm} กม.` : `${r.name ?? ""} ลึก ${Math.round(r.maxDepthCm)} ซม.${r.distKm != null ? ` · ห่าง ${r.distKm.toFixed(1)} กม.` : ""}`,
          src: `สนน. กทม. ผ่าน POPNIX${r.measuredAt ? " · " + thTime(r.measuredAt) : ""}`, target: "#gauge-host" });
      }
    }
    const pr = pendingRow("📍", "ฝนที่บ้าน 3 วัน", s.rain, "#rain-host");
    if (pr) out.push(pr);
    else if (ok(s.rain)) {
      const rv = s.rain.value, days = rv.days.slice(0, 3), split = splitDays(rv.days);
      const top = days.reduce((a, b) => ((RANK[b.level] ?? 0) > (RANK[a.level] ?? 0) ? b : a), days[0]!);
      const shown = split[0] ?? top; // D4: the median level + whether the models agree
      const agree = split.length ? ` · แบบจำลองยังเห็นไม่ตรงกัน (${shown.dow} ${shown.min}–${shown.max} มม.)` : ` (${shown.dow} ${shown.min}–${shown.max} มม. · ${shown.m} แบบจำลอง)`;
      const fb = rv.fallback?.kind === "province" ? `ค่าประมาณระดับจังหวัด${rv.fallback.name ?? ""} · ` : "";
      out.push({ icon: "📍", title: `${placeWord()} · ฝน 3 วัน`, worry: (RANK[top.level] ?? 0) >= 3 || split.length > 0,
        text: `${RAIN_TH[shown.level] ?? shown.level}${agree}`,
        src: `${fb}Open-Meteo · อัปเดต ${thTime(rv.updatedAt)}`, target: "#rain-host" });
    }
    // notices: ปภ. (district) + สทนช. (province)
    if (s.place.state === "loading" || s.lowerRiver.state === "loading") out.push({ icon: "📍", title: "ประกาศ ปภ./สทนช.", text: LOADING_TXT, worry: null, src: "", target: "#flood-note" });
    else {
      const parts: string[] = []; let worry = false;
      if (ok(s.place) && s.place.value.declared) {
        const pl = s.place.value;
        parts.push(`ปภ.: อำเภอนี้มีประกาศเขตภัยพิบัติ${pl.declaredDate ? ` (ประกาศ ปภ. ณ ${thBE(pl.declaredDate)})` : ""}${ddpmStale(pl.declaredDate) ? " · ยังไม่ได้ตรวจการยกเลิกประกาศหลังวันนี้" : ""}`);
        worry = true;
      }
      if (ok(s.lowerRiver)) for (const a of s.lowerRiver.value.alerts) { parts.push(`สทนช.: ${a.title} ${thTime(a.from)} – ${thTime(a.to)}`); worry = true; }
      const known = ok(s.place) || ok(s.lowerRiver);
      out.push({ icon: "📍", title: `${placeWord()} · ประกาศ`, worry: known ? worry : null,
        text: parts.length ? parts.join(" · ") : known ? "ไม่มีประกาศ ปภ./สทนช. ที่ยังมีผลกับพื้นที่นี้" : NODATA_TXT,
        src: "ปภ. · สทนช.", target: ok(s.lowerRiver) && s.lowerRiver.value.alerts.length ? "#lower-river" : "#flood-note" });
    }
  }
  // 🌀 storms — only when the data has one
  if (ok(s.storm) && s.storm.value.count > 0) {
    const x = s.storm.value;
    const km = x.nearestKm != null ? Math.round(x.nearestKm) : "?";
    const more = x.count > 1 ? ` (และอีก ${x.count - 1} ลูก)` : "";
    // a watch-only list (no track reaching Thailand) is stated plainly and is not a "worry" row
    out.push({ icon: "🌀", title: `พายุ ${x.name ?? ""}`, worry: x.affects,
      text: x.affects ? `ห่างไทย ~${km} กม.${more}` : `ยังไม่มีรายงานเข้าไทย · เฝ้าติดตาม ~${km} กม.${more}`,
      src: `GDACS (EC JRC)${x.updatedAt ? " · " + thTime(x.updatedAt) : ""}`, target: "#map" });
  }
  // 🌊 sea surge — only on the tidal reach
  if (ok(s.lowerRiver) && s.lowerRiver.value.tidal) {
    const t = s.lowerRiver.value.tidal, st0 = t.stations[0];
    const over = t.stations.find((x) => x.max24 && x.bankM != null && x.max24.v > x.bankM);
    out.push({ icon: "🌊", title: `น้ำทะเลหนุน · แม่น้ำ${t.river}ตอนล่าง`, worry: !!over,
      text: over ? `${over.name}: สูงสุด 24 ชม. สูงกว่าตลิ่ง ${(over.max24!.v - over.bankM!).toFixed(2)} ม.` : st0?.max24 ? `${st0.name}: สูงสุด 24 ชม. ${st0.max24.v.toFixed(2)} ม.รทก.` : "ระดับน้ำขึ้นลงตามรอบน้ำทะเล",
      src: `ThaiWater (สสน.)${st0?.latest ? " · " + thTime(st0.latest.t) : ""}`, target: "#lower-river" });
    // v0.9.10 A: today's / yesterday's measured highest reading at the station nearest the point (else the mouth)
    const ps = t.stations.find((x) => x.role === "near" && peaksText(x.peaks)) ?? t.stations.find((x) => peaksText(x.peaks));
    const pt = ps ? peaksText(ps.peaks) : null;
    if (pt) out.push({ icon: "🌊", title: `น้ำทะเลหนุน · ${ps!.name}`, worry: null, text: pt, src: "ThaiWater (สสน.) · ระดับน้ำวัดจริง", target: "#lower-river" });
  }
  return out;
}

/* ── the sheet ────────────────────────────────────────────────────────── */
let modal: HTMLElement | null = null;
let unsub: (() => void) | null = null;
let lastFocus: HTMLElement | null = null;

function placeTitle(s: SummaryState): string {
  if (ok(s.place)) { const p = s.place.value; return p.bkk ? `แขวง${p.t} เขต${p.a}` : `ต.${p.t} อ.${p.a}`; }
  return s.centerLabel?.replace(/^📍\s*/, "") ?? "จุดนี้";
}

/** Share text (SPEC 2.5): "<ตำบล อำเภอ>: <ระดับ> — <เหตุผล> · ข้อมูล ณ <เวลา> · <ลิงก์>". */
export function summaryShareText(s: SummaryState, r: SummaryResult): string {
  const why = r.reasons.length ? r.reasons.slice(0, 2).join(" · ") : r.missing.length ? `ขาดข้อมูล: ${r.missing.join(", ")}` : "";
  return `${placeTitle(s)}: ${r.label}${why ? " — " + why : ""} · ข้อมูล ณ ${nowTh()} · ${s.from ? buildShareUrl(s.from, s.radiusKm) : ""}`;
}

function render(): void {
  if (!modal) return;
  const s = summaryState();
  // v0.9.10 A: in a flooded / ปภ.-declared area a falling trend is never shown (same rule as the lower-river card) — marked for the e2e gate
  modal.querySelector<HTMLElement>(".sum-panel")?.setAttribute("data-hide-fall", hideFall(s) ? "1" : "0");
  const r = decideSummary(inputsOf(s));
  const ui = LEVEL_UI[r.level];
  const why = r.level === "insufficient" ? `ขาดข้อมูล: ${r.missing.join(" · ")}`
    : r.level === "pending" ? "กำลังรวบรวมข้อมูลหลัก…" : r.reasons.length ? `เพราะ: ${r.reasons.slice(0, 2).join(" · ")}` : "";
  const rs = rows(s);
  const deepest = ok(s.roadGauge) ? s.roadGauge.value.maxDepthCm : null;
  const depth = deepest != null && deepest > 0 ? depthAdviceCm(deepest) : null;
  const body = modal.querySelector<HTMLElement>(".sum-body")!;
  body.innerHTML = `
    <div class="sum-level ${ui.cls}" role="status" aria-live="polite">
      <div class="sum-level-h"><span aria-hidden="true">${ui.icon}</span> ${esc(r.label)}${r.provisional && r.level !== "pending" ? ' <span class="muted small">(ยังโหลดไม่ครบ)</span>' : ""}</div>
      ${why ? `<div class="sum-why">${esc(why)}</div>` : ""}
    </div>
    <h3 class="sum-h">น้ำมาถึง${placeWord()}ยังไง</h3>
    <ol class="sum-path">
      ${rs.map((x, i) => `<li><button class="sum-row" data-i="${i}" type="button">
        <span class="sum-ico" aria-hidden="true">${x.icon}</span>
        <span class="sum-main"><b>${esc(x.title)}</b> <span class="${x.worry === null ? "muted" : ""}">${esc(x.text)}</span>
          ${x.src ? `<span class="sum-src">${esc(x.src)}</span>` : ""}</span>
        <span class="sum-dot ${x.worry ? "on" : ""}" aria-label="${x.worry ? "เป็นเหตุให้ต้องห่วง" : "ไม่เป็นเหตุ"}">${x.worry === null ? "" : x.worry ? "●" : "○"}</span>
      </button></li>`).join("")}
      ${elevRow(s)}
    </ol>
    ${ok(s.place) && s.place.value.declared && ddpmStale(s.place.value.declaredDate)
      ? `<div class="muted small sum-note">ประกาศ ปภ. ณ ${esc(thBE(s.place.value.declaredDate) ?? "")} — ยังไม่ได้ตรวจการยกเลิกประกาศหลังวันนี้ · <a href="${DDPM_URL}" target="_blank" rel="noopener">ดูเว็บ ปภ. ↗</a></div>` : ""}
    ${r.todo || depth ? `<h3 class="sum-h">ควรทำ</h3>
      ${r.todo ? `<div class="sum-todo">${esc(r.todo.replace(/ใกล้คุณ/g, nearWord()))}</div>` : ""}
      ${depth ? `<div class="sum-todo">${depth.icon} จุดวัดถนน${nearWord()} ${Math.round(deepest!)} ซม. (${esc(depth.label)}): ${telize(depth.advice)}</div>` : ""}` : ""}
    <div class="sum-emerg"></div>
    <div class="sum-share">
      <button class="btn primary" type="button" data-act="line">📤 แชร์สรุปนี้ทาง LINE</button>
      <button class="btn" type="button" data-act="copy">คัดลอกข้อความ</button>
    </div>
    <div class="muted small sum-foot">สรุปเมื่อ ${esc(nowTh())} · แตะแต่ละบรรทัดเพื่อดูที่มา</div>`;
  body.querySelector(".sum-emerg")!.append(emergencyCard());
  body.querySelectorAll<HTMLButtonElement>(".sum-row").forEach((b) => b.addEventListener("click", () => {
    const row = rs[Number(b.dataset.i)];
    if (row) openDetail(row.target);
  }));
  body.querySelectorAll<HTMLButtonElement>("[data-act]").forEach((b) => b.addEventListener("click", () => {
    const text = summaryShareText(s, r);
    if (b.dataset.act === "line") void shareToLine(text).then((how) => { if (how === "copied") flash(b); });
    else void copyText(text, b);
  }));
}

async function copyText(text: string, btn: HTMLElement): Promise<void> {
  try { await navigator.clipboard.writeText(text); }
  catch { const ta = document.createElement("textarea"); ta.value = text; document.body.append(ta); ta.select(); document.execCommand("copy"); ta.remove(); }
  flash(btn);
}
function flash(btn: HTMLElement): void { const prev = btn.textContent; btn.textContent = "คัดลอกแล้ว ✓ วางใน LINE ได้เลย"; setTimeout(() => { btn.textContent = prev; }, 1800); }

/** Tap a row → close the summary and show that dimension's detail card. */
function openDetail(selector: string): void {
  closeSummary();
  if (selector === "#map") { window.dispatchEvent(new Event("fs-show-weather")); return; } // storms live on the map → reveal the weather overlay
  window.dispatchEvent(new Event("fs-open-detail"));
  setTimeout(() => {
    const t = document.querySelector<HTMLElement>(selector);
    if (!t) return;
    const det = t.closest("details"); if (det) det.open = true;
    t.scrollIntoView({ behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "center" });
    t.classList.add("sum-flash"); setTimeout(() => t.classList.remove("sum-flash"), 1600);
  }, 260);
}

export function openSummary(): void {
  if (!hasPoint()) return;
  if (modal) { render(); return; }
  lastFocus = document.activeElement as HTMLElement | null;
  modal = document.createElement("div");
  modal.className = "sum-modal";
  modal.setAttribute("role", "dialog"); modal.setAttribute("aria-modal", "true"); modal.setAttribute("aria-labelledby", "sum-title");
  modal.innerHTML = `<div class="sum-panel">
      <div class="sum-head"><h2 id="sum-title">สรุปสำหรับ ${esc(placeTitle(summaryState()))}</h2>
        <button class="sum-close" type="button" aria-label="ปิดสรุป">✕</button></div>
      <div class="sum-body"></div></div>`;
  modal.querySelector(".sum-close")!.addEventListener("click", closeSummary);
  modal.addEventListener("click", (e) => { if (e.target === modal) closeSummary(); });
  document.addEventListener("keydown", onKey);
  document.body.append(modal);
  render();
  unsub = onSummaryChange(() => {
    if (!modal) return;
    if (!hasPoint()) { closeSummary(); return; }
    const h = modal.querySelector("#sum-title"); if (h) h.textContent = `สรุปสำหรับ ${placeTitle(summaryState())}`;
    render();
  });
  (modal.querySelector(".sum-close") as HTMLElement).focus();
}
export function closeSummary(): void {
  if (!modal) return;
  unsub?.(); unsub = null;
  document.removeEventListener("keydown", onKey);
  modal.remove(); modal = null;
  lastFocus?.focus?.();
}
function onKey(e: KeyboardEvent): void { if (e.key === "Escape") closeSummary(); }

/* v0.9.10 B: "⛰ จุดนี้สูงประมาณ N ม. (≥ 10 ม.; ต่ำกว่า → "จุดนี้เป็นที่ราบลุ่ม ใกล้ระดับน้ำทะเล") · สูงกว่า/ต่ำกว่า/ใกล้เคียง พื้นที่รอบ 1 กม. โดยเฉลี่ย" — information only
   (not a summary input); no value → the old "ยังไม่มีข้อมูล" line */
function elevRow(s: SummaryState): string {
  const e = s.elevation;
  if (e.state !== "ok") return `<li class="sum-missing"><span class="sum-ico" aria-hidden="true">⛰</span><span class="muted">${e.state === "loading" ? "กำลังโหลด… ความสูงพื้นที่" : "ยังไม่มีข้อมูล: ความสูงพื้นที่"}</span></li>`;
  const n = Math.round(e.value.elevationM);
  // v0.9.10b: under 10 m no number (ELEV_LOWLAND_M) — "จุดนี้เป็นที่ราบลุ่ม ใกล้ระดับน้ำทะเล"
  const h = n < ELEV_LOWLAND_M ? `เป็น${LOWLAND_TEXT}` : `สูงประมาณ ${n} ม.`;
  const cmp = e.value.compare === "higher" ? "สูงกว่า" : e.value.compare === "lower" ? "ต่ำกว่า" : e.value.compare === "similar" ? "ใกล้เคียง" : null;
  return `<li class="sum-elev"><span class="sum-ico" aria-hidden="true">⛰</span><span class="sum-main"><b>${placeWord()}${h}</b>${cmp ? ` <span>· ${cmp} พื้นที่รอบ 1 กม. โดยเฉลี่ย</span>` : ""}</span></li>`;
}

/** v0.9.10 A: " · กำลังขึ้น X ซม./ชม." after the river row's station — a fall is never shown in a flooded /
 *  ปภ.-declared area (same rule as the lower-river card) */
function trendTail(s: SummaryState, station: string): string {
  if (s.stationTrend.state === "loading") return " · กำลังโหลดแนวโน้ม…";
  if (!ok(s.stationTrend)) return " · ข้อมูลไม่พอบอกแนวโน้ม"; // no series / no station id → never a guessed trend
  if (s.stationTrend.value.name !== station) return "";
  const t = s.stationTrend.value;
  if (t.kind === "down" && hideFall(s)) return "";
  return ` · ${trendText({ kind: t.kind, cmPerHour: t.cmPerHour, n: 0, expected: 0 })}`;
}
const hideFall = (s: SummaryState): boolean =>
  (ok(s.gistda) && (s.gistda.value.level === "flooded" || s.gistda.value.level === "near")) || (ok(s.place) && s.place.value.declared);
